import {
    AfterViewInit,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {HierarchyService} from '../../../shared/hierarchy.service';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {productionPODValidator, validateNotBlank, validatePOD} from '../../../commons/validators';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../../shared/entity-names';
import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {debounceTime, filter, first, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Phases, RateMode, VoltageLevel} from 'smag-commons/cfg';
import {POD_MAX_CHARS} from '../../../shared/limits';
import {ProducerPodStatisticalProfileRequest} from 'smag-commons/dto/api';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {
    ECMember,
    ECMemberAccessType,
    ECMemberParticipationType,
    EnergyCommunity,
    EnergyCommunityNamedPod,
    ENTITY_METADATA,
    EntitySubType,
    EntityType,
    PlantStatisticalProfile,
    PodStatisticalProfile,
    UpdateECMemberRequest
} from 'smag-commons/cfg/hierarchy';
import {ECMemberPOD, EnergyCommunityNamedPlant} from 'smag-commons/cfg/hierarchy/ec';
import {
    PredictiveModelPlantResource,
    ResetContext as PredictiveModelResetContext
} from '../../../shared/predictive-model/predictive-model.component';
import {
    ResetContext as StatisticalProfileResetContext,
    StatisticalProfilePodResource,
    StatisticalProfileResourceType
} from '../../../shared/statistical-profile/statistical-profile.component';
import {WizardPodCardMode} from '../../../shared/wizard-pod-card/wizard-pod-card.component';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {getContractualPowerValues, isBlank, isDefined} from '../../../commons/commons';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import * as _ from 'lodash';
import {
    EnergyProfiles,
    getContractualPowerEnum,
    getEnergyProfile,
    getEnterData,
    getEntitySubtype,
    getFormControl,
    getIncentiveTypes,
    getNominalPower,
    getPlantName,
    getPodCode,
    getPodStatisticalProfileRequestPlantType,
    getPodUtilityTypes,
    getPredictiveModel,
    getPredictiveModelResourceType,
    getStatisticalProfile,
    hasDate,
    isControlOK,
    mkErrorMessage,
    mkPodStatisticalProfileResetValueProvider,
    mkStatisticalProfilePlantResource,
    mkStatisticalProfilePodType,
    mkTextField,
    PLANT_TYPES,
    PlantType,
    POD_PHASES,
    POD_VOLTAGES,
    PV_PLANT_TYPE,
    WIND_PLANT_TYPE
} from 'src/app/shared/energy-community';
import {CAPITAL_GRANT_MAX, CAPITAL_GRANT_MIN, limitDecimals, UPPER_INCENTIVE_BOUNDARY} from '../../../shared/ec-economics';
import {PodStatisticalProfileRequestPlant} from 'smag-commons/dto/api/profiles';
import {getRateMode} from '../../../shared/statistical-profiles';

const VERTICAL_STEPPER_VIEW = 850;

export interface PodCreatorEnergyCommunity extends EnergyCommunity {
    id: string;
}

@Component({
    selector: 'app-ec-pod-creator-wizard',
    templateUrl: './ec-pod-creator-wizard.component.html',
    styleUrls: ['./ec-pod-creator-wizard.component.scss']
})
export class EcPodCreatorWizardComponent implements OnInit, OnDestroy, AfterViewInit {
    private destroyed = new Subject<void>();
    private currentEcMemberPod: ECMemberPOD = null;
    private currentEnergyProfile: EnergyProfiles;
    private timeZone: string = null;

    @ViewChild('adviceDialog') private adviceDialog: TemplateRef<any>;
    @ViewChild('adviceDialog2') private energyProfileAdviceDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('setDate') private setDateDialog: TemplateRef<any>;
    @Output() closeDialog: EventEmitter<void> = new EventEmitter<void>();
    @Output() podResource: EventEmitter<EnergyCommunityNamedPod> = new EventEmitter<EnergyCommunityNamedPod>();
    @Input() ec: PodCreatorEnergyCommunity;
    @Input() pod: EnergyCommunityNamedPod;
    @Input() member: ECMember;

    step1Form: FormGroup;
    step2Form: FormGroup;
    step3Form: FormGroup;
    setVerticalStepper = false;
    step2Busy = false;

    readonly energyProfiles = Object.values(EnergyProfiles);
    readonly window = window;
    readonly EnergyProfiles = EnergyProfiles;
    readonly WizardPodCardMode = WizardPodCardMode;
    readonly ECMemberParticipationType = ECMemberParticipationType;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly UPPER_INCENTIVE_BOUNDARY = UPPER_INCENTIVE_BOUNDARY;
    readonly PLANT_TYPES = PLANT_TYPES;
    readonly ENTITY_NAME_MAX_LENGTH = ENTITY_NAME_MAX_LENGTH;
    readonly POD_VOLTAGES = POD_VOLTAGES;
    readonly POD_PHASES = POD_PHASES;
    readonly WIND_PLANT_TYPE = WIND_PLANT_TYPE;
    readonly contractualPowerValues = getContractualPowerValues();
    readonly getFormControl = getFormControl;
    readonly getPodUtilityTypes = getPodUtilityTypes;
    readonly limitDecimals = limitDecimals;
    readonly getIncentiveTypes = getIncentiveTypes;
    readonly hasDate = hasDate;
    readonly mkPodStatisticalProfileResetValueProvider = mkPodStatisticalProfileResetValueProvider;
    readonly entityNameError = entityNameError;
    readonly isControlOK = isControlOK;
    readonly mkStatisticalProfilePlantResource = mkStatisticalProfilePlantResource;


    constructor(private readonly i18n: I18NService,
                private fb: FormBuilder,
                private apiService: SmagApiService,
                private hierarchyService: HierarchyService,
                private readonly dialogService: DialogService,
                private readonly configurationService: ConfigurationService,
                private readonly msgBoxService: MsgBoxService) {
        this.step1Form = this.fb.group({
            podId: new FormControl(null, []),
            podName: new FormControl(null, [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)], [this.validateNameUniqueness.bind(this)]),
            podCode: new FormControl(null, [validateNotBlank, validatePOD], [ctrl => this.validatePOD(ctrl)]),
            podEnergyProfile: new FormControl(EnergyProfiles.CONSUMER, [Validators.required]),
            podVoltage: new FormControl(VoltageLevel.LOW, [Validators.required]),
            podPhase: new FormControl(Phases.SINGLE, [Validators.required]),
            contractualPower: new FormControl(null, [])
        });
        this.step2Form = this.fb.group({
            plants: this.fb.array([])
        });
        this.step3Form = this.fb.group({
            profile: new FormControl(null, [Validators.required]),
            predictiveModel: new FormControl(null, [])
        });
    }

    ngOnInit(): void {
        this.checkScreenSize();
        this.configurationService.timeConfig
            .pipe(takeUntil(this.destroyed))
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);
        if (this.member) {
            this.step1Form.addControl('podUtilityType', new FormControl(null, [Validators.required]));
        }
    }

    ngAfterViewInit(): void {
        this.currentEcMemberPod = this.member?.pods.find(pod => pod.pod === this.pod.pod.metadata[ENTITY_METADATA.POD_KEY]);
        this.step1Form.controls.podEnergyProfile.valueChanges.pipe(
            filter((value: EnergyProfiles) => value !== this.currentEnergyProfile),
            takeUntil(this.destroyed)
        ).subscribe(value => this.onChangePodEnergyProfile(value));
        if (this.isUpdate) {
            this.currentEnergyProfile = getEnergyProfile(this.pod);
            const podCode = this.pod.pod.metadata[ENTITY_METADATA.POD_KEY];
            // add plants before setting Energy profile
            this.pod.plants.forEach(plant => this.initPlant(plant));
            this.step1Form.controls.podId.setValue(this.pod.id);
            this.step1Form.controls.podName.setValue(this.pod.name);
            this.step1Form.controls.podCode.setValue(podCode);
            this.step1Form.controls.podEnergyProfile.setValue(this.currentEnergyProfile);
            this.podUtilityTypeFormHideAndSeek(this.currentEnergyProfile);
            this.step1Form.controls.podVoltage.setValue(this.pod.pod.voltage);
            this.step1Form.controls.podPhase.setValue(this.pod.pod.phases);
            this.step1Form.controls.contractualPower.setValue(getContractualPowerEnum(this.pod.pod.contractual_power));
            if (this.member) {
                this.step1Form.controls.podUtilityType.setValue(this.currentEcMemberPod?.pod_utility_type || null);
            }
            this.step3Form.controls.profile.setValue(this.pod.pod.statistical_profile);
            this.step3Form.controls.predictiveModel.setValue((this.pod.pod.predictive_models || [])[0] || null);
            // NOTE: no reset needed for POD statistical profile and predictive model, as they are already set
        } else {
            // default POD energy profile (consumer) has already been set: initialize auto predictive model
            this.onChangePodEnergyProfile(this.podEnergyProfile);
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.checkScreenSize();
    }

    get podName(): string {
        return this.step1Form.controls.podName.value;
    }

    get podCode(): string {
        return this.step1Form.controls.podCode.value;
    }

    get podVoltage(): string {
        return this.step1Form.controls.podVoltage.value;
    }

    get podPhase(): string {
        return this.step1Form.controls.podPhase.value;
    }

    get contractualPower(): number {
        return this.step1Form.controls.contractualPower.value?.value;
    }

    get podEnergyProfile(): EnergyProfiles {
        return this.step1Form.controls.podEnergyProfile.value as EnergyProfiles;
    }

    get podPredictiveModel(): PredictiveModelInfo {
        return this.step3Form.controls.predictiveModel.value;
    }

    get plantsCtrl(): FormArray {
        return this.step2Form.controls.plants as FormArray;
    }

    get plantsControls(): FormGroup[] {
        return (this.plantsCtrl).controls as FormGroup[];
    }

    get profile(): PodStatisticalProfile {
        return this.step3Form.controls.profile.value as PodStatisticalProfile;
    }

    get isMobileMember(): boolean {
        return this.member?.access_type === ECMemberAccessType.MOBILE && this.member?.participation_type === ECMemberParticipationType.MEMBER;
    }

    get isThirdPartyProducer(): boolean {
        return this.member?.participation_type === ECMemberParticipationType.THIRD_PARTY_PRODUCER;
    }

    get nameForbiddenCharacters(): string {
        return ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', ');
    }

    get isUpdate(): boolean {
        return !!this.pod;
    }

    get isStep2Enabled(): boolean {
        return this.podEnergyProfile === EnergyProfiles.PRODUCER || this.podEnergyProfile === EnergyProfiles.PROSUMER;
    }

    mkStatisticalProfilePodResource(): StatisticalProfilePodResource {
        return {
            type: StatisticalProfileResourceType.POD,
            name: this.podName,
            podType: mkStatisticalProfilePodType(this.podEnergyProfile),
            plants: this.plantsControls.map(plantForm => mkStatisticalProfilePlantResource(plantForm))
        };
    }

    mkPredictiveModelPlantResource(plantForm: FormGroup): PredictiveModelPlantResource {
        return {
            type: getPredictiveModelResourceType(plantForm.controls.plantType.value),
            nominalPower: getNominalPower(plantForm),
            predictiveModel: getPredictiveModel(plantForm)
        };
    }

    mkPlantStatisticalProfileResetValueProvider(plantForm: FormGroup): (ctx: StatisticalProfileResetContext) => Promise<PlantStatisticalProfile> {
        return ctx => this.mkPlantDefaultStatisticalProfile(plantForm.controls.nominalPower.value, ctx.rateMode || RateMode.SINGLE,
            plantForm.controls.plantType.value).toPromise();
    }


    mkPlantPredictiveModelResetValueProvider(plantForm: FormGroup): (ctx: PredictiveModelResetContext) => Promise<PredictiveModelInfo> {
        return () => this.mkPlantDefaultPredictiveModel(plantForm.controls.nominalPower.value, plantForm.controls.plantType.value).toPromise();
    }

    podError(form: FormGroup): string {
        let podErr = null;
        if (form.controls.podCode.dirty && form.controls.podCode.hasError('unique_pod')) {
            podErr = 'unique_pod';
        }
        if (form.controls.podCode.dirty && (form.controls.podCode.hasError('pod') || form.controls.podCode.hasError('empty'))) {
            podErr = 'pod';
        }
        return podErr;
    }

    mandatoryProductionPod(plantForm: FormGroup): boolean {
        // production POD is mandatory if this plant was already saved (with a production POD)
        return this.isMandatoryProductionPod(plantForm.controls.plantId.value);
    }

    getDateError(form: FormGroup): string {
        let dateErr = null;
        if (form.controls.enterData.dirty && form.controls.enterData.invalid) {
            dateErr = 'bad_date';
        }
        return dateErr;
    }

    canResetActivationDate(form: FormGroup) {
        const plant = this.pod.plants.find(p => p.id === form.controls.plantId.value);
        return plant ? !plant.plant.activation_date : true;
    }

    onClickCreatePlant() {
        this.addNewPlant();
        this.resetPodStatAndPredMod(this.podEnergyProfile).then();
    }

    onClickRemovePlant(index: number) {
        this.plantsCtrl.removeAt(index);
        this.resetPodStatAndPredMod(this.podEnergyProfile).then();
    }

    onClickResetDate(form: FormGroup) {
        form.controls.enterData.setValue(null);
    }

    onClickSetDate(form: FormGroup) {
        this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Date of commissioning of the plant'),
                template: this.setDateDialog,
                context: {timezone: this.timeZone}
            },
            {width: 900}
        ).closed.then(effectiveDate => {
            if (effectiveDate) {
                form.controls.enterData.setValue(effectiveDate.startOf('day'));
                form.controls.enterData.disable();
            }
        });
    }

    sendRequest() {
        if (this.isUpdate) {
            this.updatePod();
        } else {
            this.createPod();
        }
    }

    private createPlantForm(plantId?: string): FormGroup {
        const plantForm = this.fb.group({
            plantId: new FormControl(plantId || null, []),
            plantName: new FormControl('', [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)]),
            plantType: new FormControl(PV_PLANT_TYPE, [Validators.required]),
            podCode: new FormControl(null, [this.mkPlantPodValidator(plantId)], []),
            nominalPower: new FormControl(null, [Validators.required, Validators.min(0)]),
            capitalGrant: new FormControl(0, this.isThirdPartyProducer ? [] : [Validators.required, Validators.min(CAPITAL_GRANT_MIN), Validators.max(CAPITAL_GRANT_MAX)]),
            enterData: new FormControl(null, []),
            incentiveType: new FormControl(null, [Validators.required]),
            profile: new FormControl(null, [Validators.required]),
            predictiveModel: new FormControl(null, [])
        });
        plantForm.controls.enterData.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(plantActivationDate => {
                if (plantActivationDate && plantActivationDate.isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone))) {
                    plantForm.controls.capitalGrant.setValue(0);
                    plantForm.controls.capitalGrant.disable();
                } else {
                    plantForm.controls.capitalGrant.enable();
                }
                getIncentiveTypes(plantForm, true);  // updates types of incentive based on data entered
            });
        return plantForm;
    }

    private onChangePodEnergyProfile(profile: EnergyProfiles) {
        if (profile === EnergyProfiles.CONSUMER) {
            if (this.isUpdate &&
                (this.currentEnergyProfile === EnergyProfiles.PRODUCER || this.currentEnergyProfile === EnergyProfiles.PROSUMER)) {
                this.confirmConsumerEnergyProfile(confirm => {
                    if (confirm) {
                        this.plantsCtrl.clear();
                        this.resetPodStatAndPredMod(profile).then();
                        this.currentEnergyProfile = profile;
                    } else {
                        this.step1Form.controls.podEnergyProfile.setValue(this.currentEnergyProfile);
                    }
                });
            } else {
                this.plantsCtrl.clear();
                this.resetPodStatAndPredMod(profile).then();
                this.currentEnergyProfile = profile;
            }
        } else { // producer/prosumer
            if (this.plantsControls.length === 0) {
                this.addNewPlant();
            }
            this.resetPodStatAndPredMod(profile).then();
            this.currentEnergyProfile = profile;
        }
        this.podUtilityTypeFormHideAndSeek(profile);
    }

    private podUtilityTypeFormHideAndSeek(profile: EnergyProfiles) {
        if (this.member) {
            if (profile === EnergyProfiles.PRODUCER) {
                this.step1Form.controls.podUtilityType.setValue('');
                this.step1Form.controls.podUtilityType.disable();
            } else {
                this.step1Form.controls.podUtilityType.enable();
                this.step1Form.controls.podUtilityType.setValidators(Validators.required);
            }
        }
    }

    private initPlant(plant: EnergyCommunityNamedPlant) {
        const plantForm = this.createPlantForm(plant.id);
        plantForm.controls.plantName.setValue(plant.name);
        plantForm.controls.plantType.setValue(this.getPlantType(plant));
        plantForm.controls.plantType.disable();
        plantForm.controls.podCode.setValue(plant.plant.production_pod || null);
        plantForm.controls.nominalPower.setValue(plant.plant.nominal_power);
        plantForm.controls.capitalGrant.setValue(plant.plant.capital_grant * 100);
        plantForm.controls.enterData.setValue(plant.plant.activation_date ? moment(plant.plant.activation_date) : null);
        if (isDefined(plant.plant.incentive_mode)) {
            plantForm.controls.incentiveType.setValue(plant.plant.incentive_mode);
        }
        plantForm.controls.profile.setValue(plant.plant.statistical_profile);
        plantForm.controls.predictiveModel.setValue(plant.plant.predictive_models.length > 0 ? plant.plant.predictive_models[0] : null);
        this.updatePlantStatAndPredModOnChange(plantForm);
        this.plantsCtrl.push(plantForm);
    }

    private getPlantType(plant: EnergyCommunityNamedPlant): PlantType {
        switch (plant.plant.sub_type) {
            case EntitySubType.WIND_PLANT:
                return WIND_PLANT_TYPE;
            case EntitySubType.PV_PLANT:
            default:
                return PV_PLANT_TYPE;
        }
    }

    private createPod() {
        let plants: EnergyCommunityNamedPlant[] = [];
        if (this.isStep2Enabled) {
            plants = this.plantsControls.map(formPlant => {
                return {
                    id: formPlant.controls.plantId.value, // null if new
                    name: getPlantName(formPlant),
                    plant: {
                        type: EntityType.PLANT,
                        sub_type: getEntitySubtype(formPlant.controls.plantType.value),
                        voltage: this.podVoltage as VoltageLevel,
                        phases: this.podPhase as Phases,
                        nominal_power: getNominalPower(formPlant),
                        capital_grant: this.getCapitalGrant(formPlant),
                        activation_date: getEnterData(formPlant) ? getEnterData(formPlant).valueOf() : null,
                        incentive_mode: formPlant.controls.incentiveType.value,
                        statistical_profile: getStatisticalProfile(formPlant),
                        production_pod: !isBlank(getPodCode(formPlant)) ? getPodCode(formPlant) : null,
                        predictive_models: [getPredictiveModel(formPlant)],
                    }
                };
            });
        }
        const req: EnergyCommunityNamedPod = {
            id: this.step1Form.controls.podId.value, // null if new
            name: this.podName,
            pod: {
                type: EntityType.POD,
                voltage: this.podVoltage as VoltageLevel,
                phases: this.podPhase as Phases,
                producer: this.podEnergyProfile === EnergyProfiles.PRODUCER,
                contractual_power: this.contractualPower ? this.contractualPower : null,
                predictive_models: this.podPredictiveModel ? [this.podPredictiveModel] : null,
                statistical_profile: this.profile as PodStatisticalProfile,
                metadata: {[ENTITY_METADATA.POD_KEY]: this.podCode}
            },
            plants
        };
        const spinner = this.openSpinner('We are creating the POD');
        this.apiService.createPod(this.ec.id, req).toPromise()
            .then((response) => {
                this.closeDialog.emit();
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('POD successfully created')
                });
                spinner.closed.then(() => this.dialogService.open<any, any>({template: this.adviceDialog}));
                this.podResource.emit(response);
            })
            .catch(err => {
                const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating POD'));
                spinner.context.next({
                    loading: false,
                    loadingMessage: errMsg,
                    abort: true
                });
                this.msgBoxService.error(errMsg, err);
                throw err;
            });
    }

    private updatePod() {
        const req = _.cloneDeep(this.pod);
        const plantMap = new Map(req.plants.map(plant => [plant.id, plant]));
        let plants: EnergyCommunityNamedPlant[] = [];
        if (this.isStep2Enabled) {
            plants = this.plantsControls.map(formPlant => {
                const plantId = formPlant.controls.plantId.value;
                const activationDate = getEnterData(formPlant);
                const ecPlant = plantMap.get(plantId);
                if (plantId && ecPlant) {
                    ecPlant.plant.type = EntityType.PLANT;
                    ecPlant.plant.sub_type = getEntitySubtype(formPlant.controls.plantType.value);
                    ecPlant.plant.voltage = this.podVoltage as VoltageLevel;
                    ecPlant.plant.phases = this.podPhase as Phases;
                    ecPlant.plant.nominal_power = formPlant.controls.nominalPower.value;
                    ecPlant.plant.capital_grant =
                        this.isThirdPartyProducer ||
                        (isDefined(activationDate) && (activationDate as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone))) ?
                            0.0 : formPlant.controls.capitalGrant.value / 100;
                    ecPlant.plant.activation_date = activationDate ? activationDate.valueOf() : null;
                    ecPlant.plant.incentive_mode = formPlant.controls.incentiveType.value;
                    ecPlant.plant.statistical_profile = formPlant.controls.profile.value;
                    ecPlant.plant.production_pod = !isBlank(formPlant.controls.podCode.value) ? formPlant.controls.podCode.value : null;
                    ecPlant.plant.predictive_models = [formPlant.controls.predictiveModel.value];
                    ecPlant.name = formPlant.controls.plantName.value;
                    return ecPlant;
                } else {
                    return {
                        id: plantId, // null if new
                        name: getPlantName(formPlant),
                        plant: {
                            type: EntityType.PLANT,
                            sub_type: getEntitySubtype(formPlant.controls.plantType.value),
                            voltage: this.podVoltage as VoltageLevel,
                            phases: this.podPhase as Phases,
                            nominal_power: formPlant.controls.nominalPower.value,
                            capital_grant: this.isThirdPartyProducer || (isDefined(activationDate) && (activationDate as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone))) ?
                                0.0 : formPlant.controls.capitalGrant.value / 100,
                            activation_date: activationDate ? activationDate.valueOf() : null,
                            incentive_mode: formPlant.controls.incentiveType.value,
                            statistical_profile: formPlant.controls.profile.value,
                            production_pod: !isBlank(formPlant.controls.podCode.value) ? formPlant.controls.podCode.value : null,
                            predictive_models: [formPlant.controls.predictiveModel.value],
                        }
                    };
                }
            });
        }
        req.plants = plants;
        req.pod.type = EntityType.POD;
        req.pod.voltage = this.podVoltage as VoltageLevel;
        req.pod.phases = this.podPhase as Phases;
        req.pod.producer = this.podEnergyProfile === EnergyProfiles.PRODUCER;
        req.pod.contractual_power = this.contractualPower ? this.contractualPower : null;
        req.pod.predictive_models = this.podPredictiveModel ? [this.podPredictiveModel] : null;
        req.pod.statistical_profile = this.profile as PodStatisticalProfile;
        req.pod.metadata [ENTITY_METADATA.POD_KEY] = this.podCode;
        req.name = this.podName;

        const spinner = this.openSpinner('We are updating the POD');
        forkJoin([this.apiService.updatePod(this.ec.id, req),
            this.member ? this.apiService.updateMember(this.member.id, this.mkUpdateMemberRequest(), true) : of(null)]).toPromise()
            .then(([response, ignore]) => {
                this.closeDialog.emit();
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('POD successfully updated')
                });
                this.podResource.emit(response);
            })
            .catch(err => {
                const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating POD'));
                spinner.context.next({
                    loading: false,
                    loadingMessage: errMsg,
                    abort: true
                });
                this.msgBoxService.error(errMsg, err);
                throw err;
            });
    }

    private mkUpdateMemberRequest(): UpdateECMemberRequest {
        return {...mkUpdateMemberRequestWithoutPods(this.member, this.ec.id, this.mkUpdatedMemberPod())};
    }

    private mkUpdatedMemberPod(): ECMemberPOD[] {
        const podUtilityTypeFormValue = this.step1Form.controls.podUtilityType.value;
        if (this.currentEcMemberPod.pod_utility_type !== podUtilityTypeFormValue) { // utility type is changed
            return this.member.pods
                .map(pod => pod.pod === this.pod.pod.metadata[ENTITY_METADATA.POD_KEY] ?
                    {...pod, pod_utility_type: podUtilityTypeFormValue} : pod);
        }
        return this.member.pods;
    }

    private addNewPlant() {
        const plantForm = this.createPlantForm();
        this.updatePlantStatAndPredModOnChange(plantForm);
        this.plantsCtrl.push(plantForm);
    }

    private updatePlantStatAndPredModOnChange(plantForm: FormGroup) {
        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step2Busy = true),
                debounceTime(500),
                switchMap(nominalPower => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(nominalPower, RateMode.SINGLE, plantForm.controls.plantType.value),
                    this.mkPlantDefaultPredictiveModel(nominalPower, plantForm.controls.plantType.value)])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) =>
                this.updatePlantStatAndPredMod(plantForm, plantStatisticalProfile, plantPredictiveModel));

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                filter(() => isDefined(plantForm.controls.nominalPower.value)),
                tap(() => this.step2Busy = true),
                switchMap(() => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(plantForm.controls.nominalPower.value, RateMode.SINGLE, plantForm.controls.plantType.value),
                    this.mkPlantDefaultPredictiveModel(plantForm.controls.nominalPower.value, plantForm.controls.plantType.value)])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) =>
                this.updatePlantStatAndPredMod(plantForm, plantStatisticalProfile, plantPredictiveModel));

        plantForm.controls.profile.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step2Busy = true))
            .subscribe(() => this.resetPodStatAndPredMod(this.podEnergyProfile).then(() => this.step2Busy = false));
    }

    private updatePlantStatAndPredMod(plantForm: FormGroup,
                                      plantStatisticalProfile: PlantStatisticalProfile,
                                      plantPredictiveModel: PredictiveModelInfo) {
        if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
            plantForm.controls.profile.setValue(plantStatisticalProfile);
        } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
            plantForm.controls.profile.reset();
        }
        plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
        // reset POD's profile if plant's nominal power changes (consumers have no plants anyway)
        if (this.podEnergyProfile === EnergyProfiles.PROSUMER || this.podEnergyProfile === EnergyProfiles.PRODUCER) {
            this.resetPodStatAndPredMod(this.podEnergyProfile).then(() => this.step2Busy = false);
        } else {
            this.step2Busy = false;
        }
    }

    private resetPodStatAndPredMod(profile: EnergyProfiles): Promise<void> {
        switch (profile) {
            case EnergyProfiles.CONSUMER:
                // reset consumer's statistical profile
                this.step3Form.controls.profile.setValue(null);
                // generate auto predictive model for consumer
                return this.apiService.getConsumerPodPredictiveModel({}).toPromise()
                    .then(response => this.step3Form.controls.predictiveModel.setValue(response))
                    .catch(err => this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'), err));
            case EnergyProfiles.PRODUCER:
                // producers have no predictive model
                this.step3Form.controls.predictiveModel.setValue(null);
                // generate auto statistical profile for producer
                // if one of the plant profiles is currently invalid, POD profile is simply reset; it will be re-computed
                // when the profile becomes valid (e.g. when the nominal power of a new plant is set by the user)
                if (this.plantsControls.every(form => !!form.controls.profile.value)) {
                    const plantsStatisticalProfiles: PodStatisticalProfileRequestPlant[] = this.plantsControls
                        .map(form => ({
                            type: getPodStatisticalProfileRequestPlantType(form.controls.plantType.value),
                            profile: form.controls.profile.value
                        }));
                    const req: ProducerPodStatisticalProfileRequest = {
                        plants: plantsStatisticalProfiles,
                        rate_mode: getRateMode(plantsStatisticalProfiles)
                    };
                    return this.apiService.getProducerPodStatisticalProfile(req).toPromise()
                        .then(response => this.step3Form.controls.profile.setValue(response))
                        .catch(err => this.msgBoxService.error(this.i18n.translate('Cannot generate statistical profile for this POD'), err));
                } else {
                    // some plant is currently invalid: reset producer's statistical profile
                    this.step3Form.controls.profile.setValue(null);
                    return Promise.resolve();
                }
            case EnergyProfiles.PROSUMER:
                // reset prosumer's statistical profile
                this.step3Form.controls.profile.setValue(null);
                // generate auto predictive model for prosumer
                return this.apiService.getProsumerPodPredictiveModel({}).toPromise()
                    .then(response => this.step3Form.controls.predictiveModel.setValue(response))
                    .catch(err => this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'), err));
            default:
                return Promise.resolve();
        }
    }

    private mkPlantDefaultStatisticalProfile(nominalPower: number, rateMode: RateMode, type: PlantType): Observable<PlantStatisticalProfile> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantStatisticalProfile({nominal_power: nominalPower, rate_mode: rateMode});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantStatisticalProfile({nominal_power: nominalPower, rate_mode: rateMode, zone: this.ec.zone});
        }
    }

    private mkPlantDefaultPredictiveModel(nominalPower: number, type: PlantType): Observable<PredictiveModelInfo> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantPredictiveModel({});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantPredictiveModel({nominal_power: nominalPower});
        }
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: 700,
            hideCloseButton: true
        });
    }

    private validatePOD(ctrl: AbstractControl): Observable<{ [err: string]: boolean }> {
        const pod = mkTextField(ctrl.value);
        if (!pod) {
            return of(null);
        }
        if (this.isUpdate) { // we are updating
            const podCode = this.pod.pod.metadata[ENTITY_METADATA.POD_KEY];
            if (pod === podCode) { // pod name is equal to current pod
                return of(null);
            }
        }

        // validate POD uniqueness
        return this.apiService.checkEntityExistenceByPOD(pod).pipe(map(resp => resp.value ? {unique_pod: true} : null));
    }

    private validateNameUniqueness(ctrl: AbstractControl): Observable<ValidationErrors | null> {
        const name = ctrl.value ? ctrl.value.trim() : null;
        if (!name) {
            return of(null);
        }
        if (this.isUpdate) { // we are updating pod and the name was not really updated
            if (this.pod.name === name) {
                return of(null);
            }
        }
        const siblings = this.ec ?
            this.hierarchyService.findEntityById(this.ec.id)
                .pipe(map(entity => entity.children || [])) :
            this.hierarchyService.hierarchy
                .pipe(map(hierarchy => hierarchy.entities || []));
        return siblings.pipe(
            first(),
            map(entities => !!entities.find(sibling => sibling.name === name)),
            map(found => found ? {duplicate: true} : null)
        );
    }

    private confirmConsumerEnergyProfile(callback: (confirm: boolean) => void) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure to set "{0}" as consumer?', this.podName),
            template: this.energyProfileAdviceDialog
        }).closed.then(confirm => callback(confirm));
    }

    private mkPlantPodValidator(plantId?: string): ValidatorFn {
        return ctrl => {
            const blankErr = this.isMandatoryProductionPod(plantId) ? validateNotBlank(ctrl) : null;
            if (blankErr) {
                return blankErr;
            }
            const podErr = productionPODValidator(ctrl);
            if (podErr) {
                return podErr;
            }
            if (isBlank(ctrl.value)) {
                // no need to check uniqueness
                return null;
            }
            // check for production POD uniqueness
            const otherProductionPods: string[] = [];
            this.plantsControls.forEach(plantForm => {
                const podCodeCtrl = plantForm.controls.podCode;
                if (podCodeCtrl !== ctrl) {
                    otherProductionPods.push(podCodeCtrl.value);
                }
            });
            if (otherProductionPods.find(p => p?.trim() === ctrl.value)) {
                return {unique_pod: true};
            }
            return null;
        };
    }

    private getCapitalGrant(form: FormGroup) {
        return this.isThirdPartyProducer || this.ignoreCapitalGrant(form) ? 0.0 : form.controls.capitalGrant.value / 100;
    }

    private isMandatoryProductionPod(plantId: string): boolean {
        const savedPlant = plantId ? (this.pod.plants || []).find(plant => plant.id === plantId) : null;
        return savedPlant ? !!savedPlant.plant.production_pod : false;
    }

    private ignoreCapitalGrant(form: FormGroup) {
        return (isDefined(getEnterData(form)) && (getEnterData(form) as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone)));
    }

    private checkScreenSize() {
        this.setVerticalStepper = window.innerWidth <= VERTICAL_STEPPER_VIEW;
    }

}

function mkUpdateMemberRequestWithoutPods(member: ECMember, networkId: string, pods: ECMemberPOD[]): UpdateECMemberRequest {
    return {
        u3id: member.u3id,
        network_id: networkId,
        juridical_type: member.juridical_type,
        entity_subtype: member.entity_subtype,
        legal_form: member.legal_form,
        subject_type: member.subject_type,
        personal_data: member.personal_data, // ECMemberPersonalData,
        accepted_documents: member.accepted_documents,
        spatial_coordinates: member.spatial_coordinates, // SpatialCoordinates
        pods,
        pod_relations: member.pod_relations,
        participation_type: member.participation_type,
        access_type: member.access_type
    };
}
